import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Container, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import SectionHeading from "../SectionHeading/SectionHeading";

const rubikfont = { fontFamily: "Rubik, sans-serif" };
const API_URL = "https://api.glamcompany.in/api/v1/";

const CARD_W = { xs: 112, md: 140 };

const BrandSection = () => {
    const [brands, setBrands] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const scrollRef = useRef(null);

    useEffect(() => {
        const fetchBrands = async () => {
            try {
                const res = await fetch(`${API_URL}brand/all`, {
                    headers: { "x-app-platform": "website" }
                });
                const data = await res.json();
                let brandArr = (data.data || []).filter(b => b && b.name && b.name.trim());
                setBrands(brandArr);
            } catch (e) {
                console.error("Failed to fetch brands:", e);
            } finally {
                setLoading(false);
            }
        };
        fetchBrands();
    }, []);

    // Auto-scroll
    useEffect(() => {
        const container = scrollRef.current;
        if (!container || loading) return;

        const autoScroll = setInterval(() => {
            const { scrollLeft, scrollWidth, clientWidth } = container;
            if (scrollLeft >= scrollWidth - clientWidth - 10) {
                container.scrollTo({ left: 0, behavior: "smooth" });
            } else {
                container.scrollBy({ left: 200, behavior: "smooth" });
            }
        }, 2500);

        // Pause on hover
        const pause = () => clearInterval(autoScroll);
        container.addEventListener("mouseenter", pause);

        return () => {
            clearInterval(autoScroll);
            container.removeEventListener("mouseenter", pause);
        };
    }, [loading, brands]);

    if (!loading && brands.length === 0) return null;

    const handleBrandClick = (brandName) => {
        navigate(`/products?brand=${encodeURIComponent(brandName)}&title=${encodeURIComponent(brandName)}`);
        window.scrollTo(0, 0);
    };

    return (
        <Box sx={{ py: { xs: 2.5, md: 3.5 }, background: "linear-gradient(to bottom, #fdfbf7, #ffffff)" }}>
            <Container maxWidth="xl">
                {/* Standardized Header */}
                <SectionHeading kicker="Premium Selection" title="Shop By Brand" onAction={() => navigate("/products")} />

                {/*
                  Scrollable brand cards.
                  A horizontal scroller clips whatever sticks out of it, so the row has breathing room on every side
                  (padding, pulled back with a negative margin): a card that lifts on hover, its gold border and its
                  shadow all stay fully visible, including the first and last card.
                */}
                <Box
                    ref={scrollRef}
                    sx={{
                        display: "flex",
                        gap: { xs: 1.5, md: 2 },
                        overflowX: "auto",
                        px: 1.25,
                        pt: 1.5,
                        pb: 2.5,
                        mx: -1.25,
                        mt: -0.5,
                        scrollbarWidth: "none",
                        "&::-webkit-scrollbar": { display: "none" },
                    }}
                >
                    {loading
                        ? Array.from({ length: 6 }).map((_, i) => (
                            <Box key={i} sx={{ width: CARD_W, flexShrink: 0 }}>
                                <Skeleton variant="rectangular" sx={{ height: { xs: 132, md: 160 }, borderRadius: "18px" }} />
                            </Box>
                        ))
                        : brands.map((brandObj) => {
                            const brandName = typeof brandObj === "string" ? brandObj : (brandObj.name || "");
                            const imagePath = typeof brandObj === "object" ? brandObj.image : null;
                            const isTVFM = brandName.toLowerCase() === "tvfm";

                            return (
                                <Box
                                    key={brandName}
                                    role="link"
                                    tabIndex={0}
                                    aria-label={`Shop ${brandName}`}
                                    onClick={() => handleBrandClick(brandName)}
                                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleBrandClick(brandName); } }}
                                    className="brand-card"
                                    sx={{
                                        position: "relative",
                                        width: CARD_W,
                                        flexShrink: 0,
                                        cursor: "pointer",
                                        outline: "none",
                                        borderRadius: "18px",
                                        bgcolor: "#fff",
                                        // the border is a ring painted around the card, so it is never cut off or shifts the layout
                                        border: "1px solid #efe6d0",
                                        boxShadow: "0 2px 8px rgba(60,40,10,0.05)",
                                        overflow: "hidden",
                                        display: "flex",
                                        flexDirection: "column",
                                        transition: "transform .28s ease, box-shadow .28s ease, border-color .28s ease",
                                        "&:hover, &:focus-visible": {
                                            borderColor: "#C8A054",
                                            transform: "translateY(-4px)",
                                            boxShadow: "0 0 0 1px #C8A054, inset 0 -4px 0 0 #C8A054, 0 14px 28px rgba(200,160,84,0.24)",
                                        },
                                        "&:hover .brand-logo, &:focus-visible .brand-logo": { transform: "scale(1.07)" },
                                        "&:hover .brand-name, &:focus-visible .brand-name": { color: "#9A7428" },
                                        "&:active": { transform: "translateY(-1px)" },
                                        "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover, &:focus-visible": { transform: "none" }, "& .brand-logo": { transition: "none" } },
                                    }}
                                >
                                    {isTVFM && (
                                        <Box sx={{ position: "absolute", top: 8, left: 8, zIndex: 1, bgcolor: "#C8A054", color: "#fff", fontSize: "8px", fontWeight: 700, px: 0.75, py: 0.25, borderRadius: "4px", letterSpacing: "0.4px", ...rubikfont }}>
                                            OUR BRAND
                                        </Box>
                                    )}

                                    {/* logo */}
                                    <Box
                                        sx={{
                                            height: { xs: 92, md: 112 },
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            background: "radial-gradient(circle at 50% 40%, #ffffff 40%, #fbf5e6 100%)",
                                        }}
                                    >
                                        {imagePath ? (
                                            <Box
                                                component="img"
                                                className="brand-logo"
                                                src={`https://api.glamcompany.in${imagePath}`}
                                                alt={brandName}
                                                sx={{ width: "100%", height: "100%", objectFit: "contain", p: 1.75, transition: "transform .3s ease" }}
                                            />
                                        ) : (
                                            <Typography className="brand-logo" sx={{ fontSize: "36px", fontWeight: 800, color: "#bdb09a", transition: "transform .3s ease", ...rubikfont }}>
                                                {brandName.charAt(0).toUpperCase()}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* name (on hover the card's own bottom edge turns into a thicker gold line) */}
                                    <Box sx={{ position: "relative", px: 1, pt: 1, pb: 1.25, borderTop: "1px solid #f3ecd9", textAlign: "center" }}>
                                        <Typography
                                            className="brand-name"
                                            noWrap
                                            title={brandName}
                                            sx={{ fontSize: { xs: "11.5px", md: "12.5px" }, fontWeight: 600, color: "#333", transition: "color .2s", ...rubikfont }}
                                        >
                                            {brandName}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })
                    }
                </Box>
            </Container>
        </Box>
    );
};

export default BrandSection;
